import { useRef } from "react";

import { WebSocketClientMessage } from "@smart/bridge-types-basic";
import {
  fromEntries,
  jsonParse,
  Schema,
  Unschema,
  wrapSchema,
} from "@smart/itops-utils-basic";

type WebSocketHook<S extends MessageSchema<any, any>> = ({
  actionRouteKey,
  onMessage,
}: {
  actionRouteKey: string;
  onMessage?: (message: Unschema<S["output"]>) => void;
}) => {
  connect: () => Promise<void>;
  disconnect: () => void;
  send: (message: Unschema<S["input"]>) => Promise<void>;
  setOnMessage: (
    handler: ((message: Unschema<S["output"]>) => void) | null | undefined,
  ) => void;
};

type MessageSchema<I, O> = { input: Schema<I>; output: Schema<O> };

const buildHook =
  <S extends MessageSchema<any, any>>(
    prefix: string,
    schema: S,
    baseUrl?: string,
  ) =>
  ({
    actionRouteKey,
    onMessage,
  }: {
    actionRouteKey: string;
    onMessage?: (message: Unschema<S["output"]>) => void;
  }) => {
    const url = baseUrl
      ? `${baseUrl.replace(/\/$/, "")}/${prefix}`
      : `/${prefix}`;
    const input = wrapSchema(schema.input);
    const output = wrapSchema(schema.output);

    const socketRef = useRef<WebSocket | null>(null);
    const onMessageRef = useRef<
      ((message: Unschema<S["output"]>) => void) | null
    >(null);
    const onMessageOverriddenRef = useRef(false);
    if (!onMessageOverriddenRef.current) {
      onMessageRef.current = onMessage ?? null;
    }

    return {
      connect: () => {
        if (socketRef.current) return Promise.resolve();

        return new Promise<void>((resolve) => {
          const socket = new WebSocket(url);
          socketRef.current = socket;
          socket.addEventListener("open", () => {
            resolve();
          });
          socket.addEventListener("message", async (event) => {
            try {
              const message = await output(
                jsonParse(event.data, "parse websocket message"),
                "validate websocket received message",
              );
              onMessageRef.current?.(message);
            } catch (err) {
              console.error("Error processing WebSocket message:", err);
            }
          });
          socket.addEventListener("error", (event) => {
            console.error("WebSocket error:", event);
          });
        });
      },
      disconnect: () => {
        if (socketRef.current) {
          try {
            socketRef.current.close();
          } catch (err) {
            console.error("Error closing WebSocket:", err);
          }
          socketRef.current = null;
        }
      },
      send: async (message: Unschema<S["input"]>) => {
        if (socketRef.current) {
          const validatedMessage = await input(
            message,
            "validate websocket send message",
          );
          socketRef.current.send(
            JSON.stringify({
              action: actionRouteKey,
              data: validatedMessage,
            } as WebSocketClientMessage),
          );
        }
      },
      setOnMessage: (
        handler: ((message: Unschema<S["output"]>) => void) | null | undefined,
      ) => {
        onMessageOverriddenRef.current = true;
        onMessageRef.current = handler ?? null;
      },
    };
  };

export type UseWebsocketOptions = {
  baseUrl?: string;
};

export const useWebsocket = <
  K extends string,
  S extends { [k in K]: MessageSchema<any, any> },
>(
  prefix: string,
  keys: K[],
  schemas: S,
  options?: UseWebsocketOptions,
): { [k in K]: WebSocketHook<S[k]> } =>
  fromEntries(
    keys.map((key) => [key, buildHook(prefix, schemas[key], options?.baseUrl)]),
  );
